import { Box, Typography, alpha, useTheme } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import type { SelfSetupTourStep } from "../../utils/selfSetupTour";

type SetupStepsProgressProps = {
  steps: { key: SelfSetupTourStep; done: boolean }[];
  accent: string;
  labelFor: (key: SelfSetupTourStep) => string;
  /** When false, no step is highlighted as current. */
  highlightCurrent?: boolean;
  onStepClick?: (key: SelfSetupTourStep) => void;
};

/**
 * Numbered horizontal stepper — visual match for Zero SetupStepsProgress.
 */
export function SetupStepsProgress({
  steps,
  accent,
  labelFor,
  highlightCurrent = true,
  onStepClick,
}: SetupStepsProgressProps) {
  const theme = useTheme();
  const currentIndex = highlightCurrent
    ? steps.findIndex((s) => !s.done)
    : -1;
  const isDark = theme.palette.mode === "dark";
  const pendingBg = isDark
    ? alpha(theme.palette.common.white, 0.05)
    : alpha(theme.palette.common.black, 0.04);
  const pendingBorder = isDark
    ? alpha(theme.palette.common.white, 0.18)
    : alpha(theme.palette.common.black, 0.16);
  const connectorPending = isDark
    ? alpha(theme.palette.common.white, 0.12)
    : alpha(theme.palette.common.black, 0.1);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        width: "100%",
        maxWidth: 400,
        mx: "auto",
        mb: 3.5,
        px: 0.5,
      }}
    >
      {steps.map((step, index) => {
        const isDone = step.done;
        const isCurrent = index === currentIndex;
        const isLast = index === steps.length - 1;
        const stepNumber = index + 1;
        const doneColor = accent;
        const clickable = Boolean(onStepClick) && !isDone;

        const circleBg = isDone ? doneColor : isCurrent ? accent : pendingBg;
        const circleColor =
          isDone || isCurrent
            ? theme.palette.common.white
            : theme.palette.text.secondary;
        const circleBorder = isDone
          ? doneColor
          : isCurrent
            ? accent
            : pendingBorder;
        const labelColor = isDone
          ? doneColor
          : isCurrent
            ? accent
            : "text.disabled";

        return (
          <Box
            key={step.key}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flex: isLast ? "0 0 auto" : 1,
              minWidth: 0,
            }}
          >
            <Box
              component={clickable ? "button" : "div"}
              type={clickable ? "button" : undefined}
              onClick={
                clickable
                  ? () => {
                      onStepClick?.(step.key);
                    }
                  : undefined
              }
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: 96,
                flexShrink: 0,
                border: 0,
                background: "none",
                cursor: clickable ? "pointer" : "default",
                p: 0,
                color: "inherit",
                font: "inherit",
                borderRadius: "12px",
                "&:hover": clickable
                  ? { bgcolor: alpha(accent, 0.06) }
                  : undefined,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: circleBg,
                  color: circleColor,
                  border: "2px solid",
                  borderColor: circleBorder,
                  boxShadow: isCurrent
                    ? `0 0 0 4px ${alpha(accent, isDark ? 0.22 : 0.2)}`
                    : isDone
                      ? `0 2px 10px ${alpha(doneColor, isDark ? 0.28 : 0.3)}`
                      : "none",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                }}
              >
                {isDone ? (
                  <CheckRoundedIcon sx={{ fontSize: 20 }} />
                ) : (
                  stepNumber
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  fontWeight: isCurrent ? 700 : isDone ? 600 : 500,
                  color: labelColor,
                  textAlign: "center",
                  lineHeight: 1.3,
                }}
              >
                {labelFor(step.key)}
              </Typography>
            </Box>

            {!isLast ? (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  mt: "19px",
                  mx: 0.5,
                  borderRadius: 1,
                  bgcolor: isDone ? doneColor : connectorPending,
                }}
              />
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}
